import type * as THREE from 'three'
import type { EventBus } from '@/runtime/EventBus'
import type { ViewRegistry } from '@/shared/types/services'
import type {
  ViewCreateOptions,
  ViewExtensionKey,
  ViewExtensionSlots,
  ViewId,
  ViewRect,
  ViewSnapshot,
  ViewUpdateOptions,
} from '@/shared/types/view'
import { containsScreenPoint } from '@/shared/utils/viewRect'
import { type InternalViewRecord, ViewFactory } from './ViewFactory'

/**
 * ViewRegistry 默认实现，维护视图记录、活动视图与扩展槽。
 */
export class ViewRegistryImpl implements ViewRegistry {
  /** 主视图 ID（不可删除） */
  readonly primaryViewId: ViewId

  private readonly events: EventBus
  private readonly views = new Map<ViewId, InternalViewRecord>()
  private activeViewId: ViewId | null = null
  private nextOrder = 0
  private disposed = false

  constructor(events: EventBus, primaryViewId: ViewId = 'primary') {
    this.events = events
    this.primaryViewId = primaryViewId
  }

  /**
   * 创建视图。
   *
   * @param options - 创建选项，必须包含 rect
   * @returns 新视图快照
   */
  create(options: ViewCreateOptions): ViewSnapshot {
    this.assertNotDisposed()
    const record = ViewFactory.createRecord(options, this.nextOrder++)
    if (record.id === this.primaryViewId) {
      record.removable = false
    }
    if (this.views.has(record.id)) {
      throw new Error(`View "${record.id}" 已存在`)
    }

    this.views.set(record.id, record)
    const previousViewId = this.activeViewId
    if (this.activeViewId == null) {
      this.activeViewId = record.id
    }

    const snapshot = this.snapshotOf(record.id)
    this.events.emit('view.created', { viewId: record.id, snapshot })

    if (previousViewId !== this.activeViewId) {
      this.events.emit('view.activated', {
        viewId: record.id,
        previousViewId,
        snapshot,
      })
    }

    return snapshot
  }

  /**
   * 移除视图；主视图不可移除。
   *
   * @param id - 视图 ID
   * @returns 成功移除返回 true；未找到或为主视图返回 false
   */
  remove(id: ViewId): boolean {
    this.assertNotDisposed()
    if (id === this.primaryViewId) {
      return false
    }
    const record = this.views.get(id)
    if (!record) {
      return false
    }

    const previousActiveViewId = this.activeViewId
    this.views.delete(id)
    this.events.emit('view.removed', {
      viewId: id,
      previousActiveViewId,
    })

    if (this.activeViewId === id) {
      const nextActiveId = this.resolveFallbackActiveViewId()
      this.activeViewId = nextActiveId
      if (nextActiveId) {
        this.events.emit('view.activated', {
          viewId: nextActiveId,
          previousViewId: id,
          snapshot: this.snapshotOf(nextActiveId),
        })
      }
    }

    return true
  }

  /**
   * 按 ID 获取视图快照。
   *
   * @param id - 视图 ID
   * @returns 视图快照；未找到返回 undefined
   */
  get(id: ViewId): ViewSnapshot | undefined {
    const record = this.views.get(id)
    if (!record) {
      return undefined
    }
    return this.snapshotOf(record.id)
  }

  /**
   * 列出所有视图，按 zIndex + order 升序排列。
   *
   * @returns 视图快照数组
   */
  list(): ViewSnapshot[] {
    return Array.from(this.views.values())
      .sort((left, right) => {
        if (left.zIndex !== right.zIndex) {
          return left.zIndex - right.zIndex
        }
        return left.order - right.order
      })
      .map((record) => this.snapshotOf(record.id))
  }

  /**
   * 获取当前活动视图快照。
   *
   * @returns 活动视图快照；无活动视图返回 undefined
   */
  getActive(): ViewSnapshot | undefined {
    if (!this.activeViewId) {
      return undefined
    }
    return this.snapshotOf(this.activeViewId)
  }

  /**
   * 切换活动视图。
   *
   * @param id - 目标视图 ID
   * @returns 活动视图快照
   */
  setActive(id: ViewId): ViewSnapshot {
    this.assertNotDisposed()
    const record = this.getRequiredRecord(id)
    if (this.activeViewId === record.id) {
      return this.snapshotOf(record.id)
    }

    const previousViewId = this.activeViewId
    this.activeViewId = record.id
    const snapshot = this.snapshotOf(record.id)
    this.events.emit('view.activated', {
      viewId: record.id,
      previousViewId,
      snapshot,
    })
    return snapshot
  }

  /**
   * 更新视图部分字段。
   *
   * @param id - 视图 ID
   * @param patch - 待更新字段
   * @returns 更新后的视图快照
   */
  update(id: ViewId, patch: ViewUpdateOptions): ViewSnapshot {
    this.assertNotDisposed()
    const record = this.getRequiredRecord(id)
    const previous = this.snapshotOf(id)

    if (patch.rect) {
      record.rect = ViewFactory.cloneRect(patch.rect)
    }
    if (patch.visible !== undefined) {
      record.visible = patch.visible
    }
    if (patch.zIndex !== undefined) {
      record.zIndex = patch.zIndex
    }
    if (patch.layerMask !== undefined) {
      record.layerMask = patch.layerMask
    }
    if (patch.userData !== undefined) {
      record.userData = { ...patch.userData }
    }

    const snapshot = this.snapshotOf(id)
    this.events.emit('view.updated', {
      viewId: id,
      snapshot,
      previous,
    })
    return snapshot
  }

  /**
   * 更新视图矩形。
   *
   * @param id - 视图 ID
   * @param rect - 新矩形
   * @returns 更新后的视图快照
   */
  setRect(id: ViewId, rect: ViewRect): ViewSnapshot {
    return this.update(id, { rect })
  }

  /**
   * 更新视图可见性。
   *
   * @param id - 视图 ID
   * @param visible - 是否可见
   * @returns 更新后的视图快照
   */
  setVisible(id: ViewId, visible: boolean): ViewSnapshot {
    return this.update(id, { visible })
  }

  /**
   * 更新视图 layer 掩码。
   *
   * @param id - 视图 ID
   * @param layerMask - 新 layer 掩码
   * @returns 更新后的视图快照
   */
  setLayerMask(id: ViewId, layerMask: number): ViewSnapshot {
    return this.update(id, { layerMask })
  }

  /**
   * 更新视图层叠顺序。
   *
   * @param id - 视图 ID
   * @param zIndex - 新 zIndex
   * @returns 更新后的视图快照
   */
  setZIndex(id: ViewId, zIndex: number): ViewSnapshot {
    return this.update(id, { zIndex })
  }

  /**
   * 按屏幕坐标查找最顶层可见视图。
   *
   * @param screen - 屏幕坐标，相对容器
   * @returns 命中视图快照；无命中返回 undefined
   */
  resolveViewAtScreen(screen: THREE.Vector2): ViewSnapshot | undefined {
    const hits = Array.from(this.views.values()).filter((record) => {
      if (!record.visible) {
        return false
      }
      if (record.rect.width <= 0 || record.rect.height <= 0) {
        return false
      }
      return containsScreenPoint(record.rect, screen)
    })

    hits.sort((left, right) => {
      if (left.zIndex !== right.zIndex) {
        return right.zIndex - left.zIndex
      }
      return right.order - left.order
    })

    const topmost = hits[0]
    return topmost ? this.snapshotOf(topmost.id) : undefined
  }

  /**
   * 写入视图扩展槽。
   *
   * @param id - 视图 ID
   * @param key - 扩展槽键
   * @param value - 扩展槽值
   */
  setExtension<K extends ViewExtensionKey>(id: ViewId, key: K, value: ViewExtensionSlots[K]): void {
    this.assertNotDisposed()
    const record = this.getRequiredRecord(id)
    record.extensions[key] = value
  }

  /**
   * 读取视图扩展槽。
   *
   * @param id - 视图 ID
   * @param key - 扩展槽键
   * @returns 扩展槽值；未写入返回 undefined
   */
  getExtension<K extends ViewExtensionKey>(id: ViewId, key: K): ViewExtensionSlots[K] | undefined {
    const record = this.getRequiredRecord(id)
    return record.extensions[key]
  }

  /** 释放注册表，清空所有视图记录 */
  dispose(): void {
    this.disposed = true
    this.views.clear()
    this.activeViewId = null
  }

  private snapshotOf(id: ViewId): ViewSnapshot {
    const record = this.getRequiredRecord(id)
    return ViewFactory.toSnapshot(record, this.activeViewId === id)
  }

  private getRequiredRecord(id: ViewId): InternalViewRecord {
    const record = this.views.get(id)
    if (!record) {
      throw new Error(`View "${id}" 不存在`)
    }
    return record
  }

  private resolveFallbackActiveViewId(): ViewId | null {
    if (this.views.has(this.primaryViewId)) {
      return this.primaryViewId
    }
    const next = Array.from(this.views.values()).sort((left, right) => {
      if (left.order !== right.order) {
        return left.order - right.order
      }
      return left.zIndex - right.zIndex
    })[0]
    return next?.id ?? null
  }

  private assertNotDisposed(): void {
    if (this.disposed) {
      throw new Error('ViewRegistry 已销毁')
    }
  }
}
